import { firstValueFrom } from 'rxjs';
import last from 'lodash/last';
import {
  Component,
  ComponentFactoryResolver,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { DeliveryCalendar } from '@cakeiteasy/web-ui';
import moment from 'moment';
import {
  ngHelper,
  CieUtilsModalManagerService, AddressLocationManager, ToDoorCalendarDay, CalendarSchedule, IDropdownItem
} from '@cakeiteasy/web-utils';
import { DeliveryAvailabilityService } from '../../services/delivery-availability.service';
import { DeliveryUiHelperService } from '../../services/delivery-ui-helper.service';
import { IDeliveryAddress } from '../../../users';
import { DeliveryApiService } from '../../../api-services/services/delivery-api.service';
import {
  CART_DELIVERY_TYPES,
  DELIVERY_ORDER_DELIVERY_TYPES,
} from '../../../cart';
import {
  isGeoLocationValid,
  getFilledDeliveryFormLocation,
  getDeliveryFormGroup,
} from '../../delivery-helper';
import { LookupAddressDeliveryBase } from '../../../base-classes/lookup-address-delivery.base';

/**Deprecated*/
@Component({
  standalone: false,
  selector: 'delivery-to-door',
  templateUrl: './delivery-to-door.component.html',
  styleUrls: ['./delivery-to-door.component.scss']
})
export class DeliveryToDoorComponent extends LookupAddressDeliveryBase implements OnInit, OnChanges {
  deliveryPrices: any[];
  calendar: DeliveryCalendar = null;
  isCalendarLoading = false;

  constructor(protected addressLocation: AddressLocationManager,
              protected deliveryUi: DeliveryUiHelperService,
              protected modalManager: CieUtilsModalManagerService,
              protected componentResolver: ComponentFactoryResolver,
              private deliveryAvailability: DeliveryAvailabilityService,
              private deliveryApiService: DeliveryApiService,
              private addressManager: AddressLocationManager,
              private formBuilder: FormBuilder) {
    super(addressLocation, modalManager, componentResolver);
  }

  ngOnChanges(changes: SimpleChanges) {
    ngHelper.ifChanged(changes, 'deliveryAddresses', (deliveryAddresses: IDeliveryAddress[]) => {
      this.initByFavoriteAddress(deliveryAddresses);
    });
  }

  async ngOnInit(): Promise<void> {
    this.deliveryForm = this.formBuilder.group(getDeliveryFormGroup());
    this._subscribeOnDeliveryDate();

    if (this.deliveryOrder && this.deliveryOrder.type === CART_DELIVERY_TYPES.DELIVERY) {
      this.patchDeliveryForm(this.deliveryOrder);
      await this.initLookupAddress(this.deliveryOrder);
    }
  }

  resetDeliveryState() {
    super.resetDeliveryState();
    this.deliveryPrices = null;
    this.calendar = null;
  }

  async onAddressSelected(address: string) {
    const geoLocation = await (this.busy = firstValueFrom(this.addressManager.getLocationInfo(address, this.countryCode)));
    const { postNumber, address: { streetNumber } } = geoLocation;

    this.deliveryPrices = isGeoLocationValid(geoLocation) ? await this.getDeliveryPrice(this.bakeryId, postNumber) : null;

    if (this.deliveryPrices) {
      this.deliveryForm.patchValue(getFilledDeliveryFormLocation(geoLocation));
      this.geoLocationStatus = this.STATUS.VALID;
      await this.loadCalendar(this._getCalendarRequestDate());
    } else {
      this.geoLocationStatus = !streetNumber ? this.STATUS.MISSING_STREET_NUMBER : this.STATUS.INVALID;
    }
  }

  confirmDelivery() {
    const { COURIER: deliveryType } = DELIVERY_ORDER_DELIVERY_TYPES;
    const { DELIVERY: type } = CART_DELIVERY_TYPES;

    this.onNext.emit({
      deliveryOrder: {
        ...this.deliveryOrder,
        ...this.deliveryForm.getRawValue(),
        outlet: null,
        externalDeliveryFee: 0,
        deliveryType, type
      },
      saveCurrentDelivery: this.saveAddressOnSubmit
    });
  }

  public calendarFetcher(fromDate: Date, prevCalendar?: DeliveryCalendar) {
    const postNumber = this.deliveryForm.get('deliveryPostNumber').value;

    return this.orderId
      ? this.deliveryAvailability.getOrderDeliveryCalendar(this.orderId, postNumber, 2, fromDate, prevCalendar)
      : this.deliveryAvailability.getDeliveryCalendar(this.bakeryId, postNumber, 2, fromDate, prevCalendar);
  }

  async onMonthNavigate(monthDate: string) {
    await this.loadCalendar(monthDate, this.calendar);
  }

  async receiveScheduleFromDay(calendarDay: ToDoorCalendarDay): Promise<CalendarSchedule[]> {
    const { schedule } = last(calendarDay.variants);
    return schedule;
  }

  mapScheduleToTimeItems(schedule: CalendarSchedule[]): IDropdownItem[] {
    return schedule
      .map((item: CalendarSchedule) => ({ id: item.timeRange, label: item.timeRange, ref: item }));
  }

  findSchedule(schedule: CalendarSchedule[], timeValue: string): CalendarSchedule {
    return schedule.find(({ timeRange }) => timeRange === timeValue);
  }

  contextInit(context) {
    this.dp = context;

    setTimeout(() => {
      if (this.deliveryForm.get('deliveryDate').invalid) {
        this._scrollToDatepicker();
        context.open();
      }
    }, 500);
  }

  protected _scrollToDatepicker() {
    if (!this.datePickerLabel || !this.datePickerLabel.nativeElement) {
      return;
    }

    this.deliveryUi.scrollToDatepicker(this.datePickerLabel);
  }

  async scheduleChange(calendarDay: ToDoorCalendarDay) {
    const { deliveryAgentId } = last(calendarDay.variants);
    this.deliveryForm.get('deliveryAgent').patchValue(deliveryAgentId);

    const schedule = await this.changeScheduleFlow(calendarDay);

    if (!schedule) {
      return;
    }

    const selectedSchedule = this.findSchedule(schedule as CalendarSchedule[], this.getTimeValue());

    if (selectedSchedule) {
      this.setPrice(selectedSchedule.price);
    } else {
      setTimeout(() => this.rangeDropDown.open(), 100);
    }
  }

  timeChanged({ ref }: IDropdownItem) {
    this.setPrice(ref.price);
  }

  setPrice(price?: number) {
    if (this.overwritingDelivery) {
      this.price = this.overwritingDelivery.price || 0;
    } else {
      this.price = price || 0;
    }

    this.isPriceSet = true;
  }

  private async getDeliveryPrice(bakeryId: number, postNumber: string) {
    try {
      return await firstValueFrom(this.deliveryApiService.getBakeryDeliveryPrice(bakeryId, postNumber));
    } catch (e) {
      console.error('DELIVERY_PRICES_ERROR: ', e);
      return null;
    }
  }

  private async loadCalendar(monthDate: string, prevCalendar?: DeliveryCalendar): Promise<void> {
    const fromDate = moment(monthDate, 'YYYY-MM-DD', true).toDate();

    this.isCalendarLoading = true;

    try {
      this.calendar = await this.calendarFetcher(fromDate, prevCalendar);
      await this._handleDeliveryDateChange();
    } finally {
      this.isCalendarLoading = false;
    }
  }

  private _subscribeOnDeliveryDate() {
    this.addSubscriber(this.deliveryForm.get('deliveryDate').valueChanges
      .subscribe(async () => {
        await this._handleDeliveryDateChange();
      }));
  }

  private async _handleDeliveryDateChange() {
    const dateValue = this.deliveryForm.get('deliveryDate').value;

    if (!dateValue || !this.calendar || this.calendar.isDateInactive(dateValue)) {
      return;
    }

    const calendarDay = this.calendar.getDate(dateValue);
    if (calendarDay) {
      await this.scheduleChange(calendarDay as ToDoorCalendarDay);
    }
  }

  private _getCalendarRequestDate(): string {
    const modelDate = this.deliveryForm && this.deliveryForm.get('deliveryDate').value;
    return modelDate || moment().format('YYYY-MM-DD');
  }
}
